import { Card } from "@dropbox/dash-component-library";
import { Button } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { Cluster, useTheme } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { Twinkle2Line } from "@dropbox/dig-icons/dist/mjs/assets";
import { createUxaElementId } from "@mirage/analytics/uxa";
import { Link as ExternalLink } from "@mirage/link/Link";
import { AnswerCitationCardsGrid } from "@mirage/mosaics/MultiAnswersCard/layout/AnswerCitationCardsGrid";
import { MultiAnswerCardContainerWideLoading } from "@mirage/mosaics/MultiAnswersCard/layout/MultiAnswerCardContainerWideLoading";
import { useOneColumnLayout } from "@mirage/mosaics/MultiAnswersCard/layout/useOneColumnLayout";
import {
  TruncatedLayoutState,
  useTruncatedLayout,
} from "@mirage/mosaics/MultiAnswersCard/layout/useTruncatedLayout";
import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { BetaBadge } from "@mirage/shared/badges/BetaBadge";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import { AI_LEARN_MORE_URL } from "@mirage/shared/urls";
import i18n from "@mirage/translations";
import { useResizeObserver } from "@react-hookz/web";
import classNames from "classnames";
import { memo, useCallback, useRef, useState } from "react";
import styles from "./MultiAnswerCardContainer.module.css";

import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import type { FeatureLine } from "@mirage/analytics/events/enums/feature_line";
import { PAP_Expand_DashAnswers } from "@mirage/analytics/events/types/expand_dash_answers";
import { SessionManagerName } from "@mirage/analytics/session/session-manager";
import type { LoadingState } from "@mirage/mosaics/MultiAnswersCard/useInitialConversation";
import { ChatEntryPoint } from "@mirage/mosaics/SummaryQnaPanel/SummaryQna";
import type { Connectors } from "@mirage/service-connectors/service";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import type { ReactNode, RefCallback } from "react";

export interface MultiAnswerCardContainerBaseProps {
  loadingState: LoadingState;
  followups?: ReactNode;
  contentRef: RefCallback<HTMLDivElement>;
  connectors: Connectors;
  answer: ReactNode;
  pinnedAnswer?: ReactNode;
  pinnedAnswerLoadingState?: string;
  citations?: ReactNode;
  commonPapFields: {
    queryString: string;
    answerId: string;
    entryPoint: ChatEntryPoint;
    featureLine: FeatureLine;
    searchQueryUuid: string;
    searchSessionId?: string;
    searchRequestId?: string;
    conversationId: string;
  };
}
interface MultiAnswerCardContainerProps
  extends MultiAnswerCardContainerBaseProps {
  variant: "narrow" | "wide";
}
export const MultiAnswerCardContainer = memo(
  ({ variant, ...props }: MultiAnswerCardContainerProps) => {
    switch (variant) {
      case "narrow":
        return <MultiAnswerCardContainerNarrow {...props} />;
      case "wide":
        return <MultiAnswerCardContainerWide {...props} />;
      default:
        variant satisfies never;
        throw new Error(`Invalid variant: ${variant}`);
    }
  },
);
MultiAnswerCardContainer.displayName = "MultiAnswerCardContainer";

const MultiAnswerCardContainerNarrow = memo(
  ({
    loadingState,
    contentRef: contentRefCb,
    followups,
    answer,
  }: MultiAnswerCardContainerBaseProps) => {
    const isMobile = useIsMobileSize();
    const isDesktop = EnvCtx.surface === "desktop";
    const loading = shouldShowLoading(loadingState);

    const { cardHeight, contentRef: cardHeightContentRef } =
      useNarrowCardHeight(loading);
    const contentRef = useCallback(
      (element: HTMLDivElement | null) => {
        cardHeightContentRef(element);
        contentRefCb(element);
      },
      [contentRefCb, cardHeightContentRef],
    );

    return (
      <Card
        variant="outline"
        loading={loading}
        className={classNames(styles.container, styles.isNarrow, {
          [styles.isMobile]: isMobile,
          [styles.isDesktop]: isDesktop,
        })}
        style={{
          maxHeight: `${cardHeight}px`,
          transition: "max-height 750ms cubic-bezier(0.17, 0, 0.09, 1)",
        }}
      >
        <Card.Header
          withMargin={!loading}
          title={
            <Cluster>
              <Cluster.Item>
                <Text tagName="h2" variant="label" isBold>
                  {i18n.t("answers_header_title")}
                </Text>
              </Cluster.Item>
              <Cluster.Item>
                <BetaBadge className={styles.badge} />
              </Cluster.Item>
            </Cluster>
          }
        />
        <div
          ref={contentRef}
          className={classNames("meticulous-redact-recording", {
            [styles.cardContent]: !loading,
          })}
        >
          {answer}
          {followups && (
            <div className={styles.followUpContainer}>{followups}</div>
          )}
          {/* "Experimental feature" disclaimer whenever we're not loading */}
          {!loading && (
            <>
              <Text
                size="small"
                className={styles.experimentalNoticeText}
                color="faint"
              >
                {i18n.t("answers_disclaimer_text")}
                &nbsp;
                <ExternalLink
                  variant="monochromatic"
                  href={AI_LEARN_MORE_URL}
                  data-uxa-log={createUxaElementId("learn_more_link", {
                    actionSurfaceComponent: "inline_answer",
                    featureLine: "multi_answers",
                  })}
                >
                  {i18n.t("learn_more")}
                </ExternalLink>
              </Text>
            </>
          )}
        </div>
      </Card>
    );
  },
);
MultiAnswerCardContainerNarrow.displayName = "MultiAnswerCardContainerNarrow";

const ESTIMATED_NARROW_CARD_HEIGHT = 80;

function useNarrowCardHeight(loading: boolean) {
  // Using a state rather than passing refs to deal with ref changing after first render
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [cardHeight, setCardHeight] = useState(0);
  const handleResize = () => {
    if (!contentElement) {
      return;
    }
    setCardHeight(contentElement.clientHeight + ESTIMATED_NARROW_CARD_HEIGHT);
  };
  useResizeObserver(contentElement, handleResize);
  return {
    cardHeight: loading ? ESTIMATED_NARROW_CARD_HEIGHT : cardHeight,
    contentRef: setContentElement,
  };
}

const MultiAnswerCardContainerWide = memo(
  ({
    loadingState,
    contentRef,
    followups,
    connectors,
    answer,
    pinnedAnswer,
    pinnedAnswerLoadingState,
    citations,
    commonPapFields,
  }: MultiAnswerCardContainerBaseProps) => {
    const isMobile = useIsMobileSize();
    const { reportSessionAwarePapEvent } = useMirageAnalyticsContext();
    const isDesktop = EnvCtx.surface === "desktop";
    const isDarkMode = useTheme().mode === "dark";
    const hideIrrelevantAnswers =
      useFeatureFlagValue("dash_web_2025_05_08_hide_irrelevant_answers") ===
      "ON";
    const {
      truncationState,
      setToggleExpanded,
      contentRef: truncatedLayoutContentRef,
    } = useTruncatedLayout();
    const contentRefCb = useCallback(
      (element: HTMLDivElement | null) => {
        truncatedLayoutContentRef(element);
        contentRef(element);
      },
      [contentRef, truncatedLayoutContentRef],
    );
    const containerRef = useRef<HTMLDivElement>(null);
    const oneColumnLayout = useOneColumnLayout(containerRef);
    const loadingStates = hideIrrelevantAnswers
      ? ["initiating", "streaming", "not_found"]
      : ["initiating"];
    const isPinnedAnswerLoading = pinnedAnswerLoadingState === "loading";

    return (
      <div
        className={classNames(
          styles.container,
          styles.isWide,
          getTruncationClassName(truncationState),
          {
            [styles.isMobile]: isMobile,
            [styles.isDesktop]: isDesktop,
            [styles.isDarkMode]: isDarkMode,
            [styles.isLoading]: shouldShowLoading(loadingState),
            [styles.hasPinnedAnswer]: !!pinnedAnswer,
          },
        )}
        ref={containerRef}
      >
        {loadingStates.includes(loadingState) ? (
          <div className={styles.mainContent}>
            <MultiAnswerCardContainerWideLoading connectors={connectors} />
          </div>
        ) : (
          <>
            <div className={styles.mainContent}>
              <Cluster
                gap="8px"
                alignY="center"
                className={styles.titleContainer}
              >
                <Cluster.Item className={styles.titleIcon}>
                  <UIIcon src={Twinkle2Line} />
                </Cluster.Item>
                <Cluster.Item>
                  <Text size="medium" color="subtle">
                    {i18n.t("answers_header_title")}
                  </Text>
                </Cluster.Item>
                <Cluster.Item>
                  <BetaBadge className={styles.badge} />
                </Cluster.Item>
              </Cluster>
              <div className={styles.contentContainer}>
                <div ref={contentRefCb}>
                  {pinnedAnswer}
                  {answer}
                  {oneColumnLayout && citations && (
                    <AnswerCitationCardsGrid
                      singleRow={true}
                      cards={citations}
                    />
                  )}
                </div>
              </div>
              <div className={styles.mainContentFooter}>
                {/* Show follow ups when not truncated */}
                {followups && truncationState !== "auto-truncated" && (
                  <div className={styles.followUpContainer}>{followups}</div>
                )}
                {/*Show See More button when truncated */}
                {truncationState === "auto-truncated" &&
                  !isPinnedAnswerLoading && (
                    <div>
                      <Button
                        className={styles.seeMoreButton}
                        variant="filled"
                        size="large"
                        fullWidth
                        onClick={() => {
                          setToggleExpanded(true);
                          reportSessionAwarePapEvent(
                            [SessionManagerName.Search],
                            (sessionAwareness) =>
                              PAP_Expand_DashAnswers({
                                ...commonPapFields,
                                featureLine: "assist",
                                actionSurface: "serp",
                                dashActionSurface: "serp",
                                ...sessionAwareness,
                              }),
                          );
                        }}
                      >
                        {i18n.t("answers_see_more")}
                      </Button>
                    </div>
                  )}
                {/* "Experimental feature" disclaimer */}
                {!isPinnedAnswerLoading && (
                  <Text
                    size="small"
                    className={styles.experimentalNoticeText}
                    color="faint"
                  >
                    {i18n.t("assist_ai_prompt_disclaimer")}
                    &nbsp;
                    <ExternalLink
                      variant="monochromatic"
                      href={AI_LEARN_MORE_URL}
                      target="_blank"
                      data-uxa-log={createUxaElementId("learn_more_link", {
                        actionSurfaceComponent: "inline_answer",
                        featureLine: "multi_answers",
                      })}
                    >
                      {i18n.t("assist_ai_prompt_external_link")}
                    </ExternalLink>
                  </Text>
                )}
              </div>
            </div>
            {!oneColumnLayout && (
              <div className={styles.sidebarContent}>
                {citations && (
                  <AnswerCitationCardsGrid
                    singleRow={false}
                    cards={citations}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  },
);
MultiAnswerCardContainerWide.displayName = "MultiAnswerCardContainerWide";

export const MultiAnswerCardContainerDivider = () => {
  return <div className={styles.divider} />;
};

function shouldShowLoading(loadingState: LoadingState) {
  switch (loadingState) {
    case "initiating":
    case "streaming":
      return true;
    case "done":
    case "error":
    case "not_found":
      return false;
    default:
      loadingState satisfies never;
      throw new Error(`Invalid loading state: ${loadingState}`);
  }
}

function getTruncationClassName(truncationState: TruncatedLayoutState) {
  switch (truncationState) {
    case "auto-truncated":
      return styles.isAutoTruncated;
    case "user-expanded":
      return styles.userExpanded;
    case "no-truncation-needed":
      return styles.noTruncationNeeded;
    default:
      truncationState satisfies never;
      throw new Error(`Invalid truncation state: ${truncationState}`);
  }
}
